import React from 'react';

const RepoList = ({ repos, loading, error, useSemanticSearch, hasSearched }) => {
  if (loading) {
    return <div className="text-center text-gray-600">加载中...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">错误：</strong>
        <span className="block sm:inline">{error.message || String(error)}</span>
      </div>
    );
  }

  if (!repos || !Array.isArray(repos) || repos.length === 0) {
    if (hasSearched) {
      return <div className="text-center text-gray-600">没有找到匹配的仓库</div>;
    } else {
      return <div className="text-center text-gray-600">请输入GitHub用户名并点击"查看Star仓库"按钮</div>;
    }
  }

  // 确保repos是数组并且具有正确的数据结构
  const normalizedRepos = repos.map(item => {
    // 如果item已经是正确的格式，直接返回
    if (item.repo && typeof item.similarity !== 'undefined') {
      return item;
    }
    // 如果item本身就是repo，包装成正确的格式
    return {
      repo: item,
      similarity: 1
    };
  });

  return (
    <div className="grid grid-cols-1 gap-6">
      {normalizedRepos.map(({ repo, similarity }) => (
        <div key={repo.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">
                <a href={repo.html_url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                  {repo.name}
                </a>
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                by <a href={repo.owner?.html_url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                  {repo.owner?.login}
                </a>
              </p>
            </div>
            
            {/* 只在使用语义搜索时显示相似度 */}
            {useSemanticSearch && similarity !== 1 && (
              <div className="ml-4 flex items-center">
                <div className="text-sm font-medium px-3 py-1 rounded-full shadow-sm" 
                  style={{
                    backgroundColor: `rgba(${Math.round(255 * (1 - similarity))}, ${Math.round(255 * similarity)}, 0, 0.2)`,
                    color: `rgb(${Math.round(255 * (1 - similarity))}, ${Math.round(255 * similarity)}, 0)`,
                    border: `1px solid rgba(${Math.round(255 * (1 - similarity))}, ${Math.round(255 * similarity)}, 0, 0.3)`
                  }}>
                  相似度: {(similarity * 100).toFixed(1)}%
                </div>
              </div>
            )}
          </div>
          
          {repo.description && (
            <p className="mt-2 text-gray-600">{repo.description}</p>
          )}
          
          <div className="mt-4 flex flex-wrap gap-2">
            {repo.language && (
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {repo.language}
              </span>
            )}
            
            {repo.topics && repo.topics.map(topic => (
              <span key={topic} className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {topic}
              </span>
            ))}
          </div>
          
          <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200 shadow-sm">
              <svg className="w-4 h-4 mr-1 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {repo.stargazers_count !== undefined ? repo.stargazers_count.toLocaleString() : '0'}
            </span>
            <span className="flex items-center bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200 shadow-sm">
              <svg className="w-4 h-4 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clipRule="evenodd" />
              </svg>
              {repo.forks_count !== undefined ? repo.forks_count.toLocaleString() : '0'}
            </span>
            <span className="flex items-center bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200 shadow-sm">
              <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              {repo.watchers_count !== undefined ? repo.watchers_count.toLocaleString() : '0'}
            </span>
            {repo.starred_at && (
              <span className="flex items-center bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200 shadow-sm" title={new Date(repo.starred_at).toLocaleString()}>
                <svg className="w-4 h-4 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                收藏于: {new Date(repo.starred_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RepoList; 